import React, { useEffect, useRef, useState } from 'react'
import { callOutcome, voiceStartBody, wakeLockSupported } from '../lib/voiceCall'

// Foreman Field Report Assistant panel (2026-11-02, staging, behind
// REACT_APP_FOREMAN_ASSISTANT_ENABLED). A chat beside the report form: the
// foreman talks (iPad dictation) or types, and Claude fills in the form ON HIS
// SCREEN via POST /api/field-reports?action=assistant. Nothing is saved here —
// the server returns an edited copy of the form and the foreman still taps
// Submit Daily Report. The conversation lives in this component's state only.
//
// VOICE MODE (REACT_APP_FOREMAN_VOICE_ENABLED, prop voiceEnabled): "Talk to the
// assistant" opens a Vapi web call (Claude as the model, the separate "Crew
// Dispatch Report Assistant (web)" assistant — never Sarah). Its tools patch a
// server-side copy of the form (assistant_sessions), which this panel polls once
// a second and shows. The form underneath stays locked for the whole call. Uses
// the Vapi PUBLIC key (REACT_APP_VAPI_PUBLIC_KEY), which is safe in a browser.
// While a call is on it holds a Screen Wake Lock (re-taken whenever the page is
// visible again — the OS drops it when hidden). If the call DROPS anyway, the
// form keeps everything and the session stays open; the next Talk RESUMES it
// with the current form + the transcript so far (src/lib/voiceCall.js).
//
// Props: accessToken, reportId, form (current on-screen report_data),
// onForm(report_data) (the page normalizes + sets it), onBusy(bool) (the page
// blocks the form underneath while a reply is coming), onClose, wide, voiceEnabled.

const ERRORS = {
  assistant_off: 'The assistant is turned off right now. Please fill in the form by hand.',
  not_configured: 'The assistant isn’t set up yet. Please fill in the form by hand.',
  already_approved: 'This report has already been approved — it can’t be changed.',
  not_authorized: 'You can’t use the assistant on this report.',
  assistant_timeout: 'The assistant took too long to answer. Try again.',
  assistant_unavailable: 'The assistant isn’t answering right now. Try again in a minute.',
  invalid_token: 'Your session expired — please sign in again.',
  missing_token: 'Your session expired — please sign in again.',
  network: 'Couldn’t reach the assistant — check your connection and try again.',
  voice_no_key: 'Voice isn’t set up yet. Type below instead.',
  voice_mic: 'The microphone is blocked. Allow the microphone for this site in your browser settings, or type below instead.',
  voice_failed: 'The voice call couldn’t connect. Type below instead, or try again in a minute.',
}
const errText = (code) => ERRORS[code] || 'Something went wrong. Try again, or fill in the form by hand.'
const VOICE_STATUS = { connecting: 'Connecting…', listening: 'Listening…', speaking: 'Speaking…' }
const POLL_MS = 1000

const isMicError = (e) => /notallowed|permission|denied|microphone/i.test(`${e?.name || ''} ${e?.message || ''} ${e?.error?.message || ''} ${e?.errorMsg || ''}`)

export default function ReportAssistantPanel({ accessToken, reportId, form, onForm, onBusy, onClose, wide, voiceEnabled }) {
  const [messages, setMessages] = useState([])   // [{ role: 'user'|'assistant', content }]
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const listRef = useRef(null)
  const started = useRef(false)

  // Voice call state. voiceLines is the spoken transcript (kept apart from the
  // typed history, which is what the typed endpoint is sent).
  const [voice, setVoice] = useState('idle')      // idle | connecting | listening | speaking
  const [voiceLines, setVoiceLines] = useState([]) // [{ role, content }]
  const [partial, setPartial] = useState(null)     // { role, content } while someone is mid-sentence
  const vapiRef = useRef(null)
  const sessionRef = useRef(null)                  // { id, version, startedAt, callId, started }
  const pollRef = useRef(null)
  const openSessionRef = useRef(null)              // { id } left open by a dropped call, reused on the next Talk
  const hadCallRef = useRef(false)                 // a call has connected in this panel -> the next one resumes
  const userEndedRef = useRef(false)               // he tapped End
  const endedReasonRef = useRef(null)              // Vapi's endedReason, from the status-update message
  const wakeRef = useRef(null)                     // the WakeLockSentinel
  const wakeWantedRef = useRef(false)              // true while a call is on
  const [wakeOk, setWakeOk] = useState(null)       // null = not tried yet, false = unsupported / refused
  const [dropped, setDropped] = useState(false)    // show "Call ended - tap Talk to pick up…"
  const inCall = voice !== 'idle'

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }
  const headersRef = useRef(authHeaders)
  headersRef.current = authHeaders

  async function ask(history) {
    setBusy(true); onBusy(true); setError(null)
    try {
      const r = await fetch('/api/field-reports?action=assistant', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ report_id: reportId, messages: history, form }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) return { error: data.error || 'network' }
      if (data.report_data) onForm(data.report_data)
      setMessages([...history, { role: 'assistant', content: data.reply || 'Okay.' }])
      return {}
    } catch (e) {
      return { error: 'network' }
    } finally {
      setBusy(false); onBusy(false)
    }
  }

  // First open: Claude greets him and asks the first question. With voice on he
  // picks first (talk or type), so no typed greeting is spent.
  useEffect(() => {
    if (started.current || voiceEnabled) return
    started.current = true
    ask([]).then(res => { if (res.error) setError(res.error) })
  }, [])

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages, busy, voiceLines, partial])

  // Never leave a call, a poll, a wake lock or an open session behind if the
  // panel goes away.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && wakeWantedRef.current) acquireWake() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      if (pollRef.current) clearInterval(pollRef.current)
      releaseWake()
      const open = sessionRef.current || openSessionRef.current
      sessionRef.current = null; openSessionRef.current = null
      try { vapiRef.current && vapiRef.current.stop() } catch (e) { /* already stopped */ }
      if (open) {
        fetch('/api/field-reports?action=assistant-voice-end', {
          method: 'POST', headers: headersRef.current, keepalive: true, body: JSON.stringify({ session_id: open.id }),
        }).catch(() => {})
      }
    }
  }, [])

  async function send() {
    const text = input.trim()
    if (!text || busy || inCall) return
    const history = [...messages, { role: 'user', content: text }].slice(-40)
    setMessages(history); setInput('')
    const res = await ask(history)
    if (res.error) {
      // Put his words back so he can resend them.
      setMessages(messages); setInput(text); setError(res.error)
    }
  }

  async function retryGreeting() {
    const res = await ask([])
    if (res.error) setError(res.error)
  }

  // ── voice ───────────────────────────────────────────────────────────────────
  async function pollSession(s = sessionRef.current) {
    if (!s) return
    try {
      const r = await fetch(`/api/field-reports?action=assistant-session&id=${encodeURIComponent(s.id)}`, { headers: authHeaders })
      const data = await r.json().catch(() => ({}))
      if (r.ok && data.ok && data.version > s.version) {
        s.version = data.version
        onForm(data.form)
      }
    } catch (e) { /* next tick tries again */ }
  }

  // Screen Wake Lock: held for the whole call. The OS releases it whenever the
  // page is hidden, so the visibilitychange handler takes it again.
  async function acquireWake() {
    wakeWantedRef.current = true
    if (!wakeLockSupported()) { setWakeOk(false); return }
    if (wakeRef.current) return
    try {
      const lock = await navigator.wakeLock.request('screen')
      if (!wakeWantedRef.current) { lock.release().catch(() => {}); return }
      wakeRef.current = lock
      if (lock.addEventListener) lock.addEventListener('release', () => { if (wakeRef.current === lock) wakeRef.current = null })
      setWakeOk(true)
    } catch (e) {
      setWakeOk(false)
    }
  }
  function releaseWake() {
    wakeWantedRef.current = false
    const lock = wakeRef.current
    wakeRef.current = null
    if (lock) lock.release().catch(() => {})
  }

  async function finishCall() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    releaseWake()
    // Take the session off the ref first: call-end and error can both land here.
    const s = sessionRef.current
    sessionRef.current = null
    const outcome = callOutcome({ userEnded: userEndedRef.current, endedReason: endedReasonRef.current })
    userEndedRef.current = false; endedReasonRef.current = null
    vapiRef.current = null
    setVoice('idle'); setPartial(null)
    if (s) {
      await pollSession(s)   // pick up the last tool result
      if (outcome === 'dropped') {
        // Leave the session open: the next Talk picks up on it.
        openSessionRef.current = { id: s.id }
        if (s.started) setDropped(true)
      } else {
        openSessionRef.current = null
        fetch('/api/field-reports?action=assistant-voice-end', {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ session_id: s.id, vapi_call_id: s.callId || null, seconds: Math.round((Date.now() - s.startedAt) / 1000) }),
        }).catch(() => {})
      }
    }
    onBusy(false)
  }

  async function startVoice() {
    if (inCall || busy) return
    setError(null); setDropped(false)
    const publicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY
    if (!publicKey) { setError('voice_no_key'); return }
    acquireWake()   // inside the tap, before any await
    setVoice('connecting'); onBusy(true)
    try {
      // A second call in this panel RESUMES: current form, open session, transcript so far.
      const body = voiceStartBody({ reportId, form, hadCall: hadCallRef.current, openSessionId: openSessionRef.current?.id, lines: voiceLines })
      const r = await fetch('/api/field-reports?action=assistant-voice-start', {
        method: 'POST', headers: authHeaders, body: JSON.stringify(body),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) { releaseWake(); setError(data.error || 'network'); setVoice('idle'); onBusy(false); return }
      openSessionRef.current = null
      sessionRef.current = { id: data.session_id, version: data.version || 0, startedAt: Date.now(), callId: null, started: false }
      userEndedRef.current = false; endedReasonRef.current = null

      // The SDK is only downloaded when he taps Talk.
      const { default: Vapi } = await import('@vapi-ai/web')
      const vapi = new Vapi(publicKey)
      vapiRef.current = vapi
      vapi.on('call-start', () => {
        hadCallRef.current = true
        if (sessionRef.current) sessionRef.current.started = true
        setVoice('listening')
        if (!pollRef.current) pollRef.current = setInterval(() => pollSession(), POLL_MS)
      })
      vapi.on('speech-start', () => setVoice('speaking'))
      vapi.on('speech-end', () => setVoice('listening'))
      vapi.on('message', (m) => {
        if (m && m.type === 'tool-calls-result') { pollSession(); return }   // a tool just changed the form
        if (m && m.type === 'status-update' && m.status === 'ended') { endedReasonRef.current = m.endedReason || null; return }
        if (!m || m.type !== 'transcript' || !m.transcript) return
        const role = m.role === 'assistant' ? 'assistant' : 'user'
        if (m.transcriptType === 'final') {
          setPartial(null)
          setVoiceLines(prev => [...prev, { role, content: m.transcript }])
        } else {
          setPartial({ role, content: m.transcript })
        }
      })
      vapi.on('call-end', () => { finishCall() })
      vapi.on('error', (e) => {
        console.error('voice call error:', e)
        // Once the call was up, an error is just a drop (the "pick up" note says
        // so); before that, say why it couldn't connect.
        if (!sessionRef.current?.started) setError(isMicError(e) ? 'voice_mic' : 'voice_failed')
        try { vapi.stop() } catch (x) { /* ignore */ }
        finishCall()
      })
      const call = await vapi.start(data.assistant_id, { variableValues: data.variable_values })
      if (call && call.id && sessionRef.current) sessionRef.current.callId = call.id
      if (!call && vapiRef.current === vapi) {
        setError('voice_failed'); finishCall()
      }
    } catch (e) {
      console.error('voice start failed:', e)
      setError(isMicError(e) ? 'voice_mic' : 'voice_failed')
      try { vapiRef.current && vapiRef.current.stop() } catch (x) { /* ignore */ }
      finishCall()
    }
  }

  function endVoice() {
    userEndedRef.current = true
    try { vapiRef.current ? vapiRef.current.stop() : finishCall() } catch (e) { finishCall() }
  }

  const shell = wide
    ? { position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, borderLeft: '1px solid #d8dde3', zIndex: 1000 }
    : { position: 'fixed', inset: 0, zIndex: 1000 }
  const bubble = (m, i, faint) => (
    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10, opacity: faint ? 0.6 : 1 }}>
      <div style={{
        maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap',
        background: m.role === 'user' ? '#2563eb' : '#fff', color: m.role === 'user' ? '#fff' : '#1f2937',
        border: m.role === 'user' ? 'none' : '1px solid #e5e7eb',
      }}>{m.content}</div>
    </div>
  )

  return (
    <div style={{ ...shell, background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, Helvetica, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#1e2d3d', color: '#fff' }}>
        <div style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>💬 Report Assistant</div>
        <button onClick={onClose} disabled={busy || inCall}
          style={{ border: '1px solid rgba(255,255,255,.5)', background: 'transparent', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 13, cursor: busy || inCall ? 'default' : 'pointer' }}>
          {wide ? 'Close' : 'Back to form'}
        </button>
      </div>

      {voiceEnabled && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
          {!inCall ? (
            <button onClick={startVoice} disabled={busy}
              style={{ width: '100%', padding: '14px 12px', fontSize: 17, fontWeight: 700, borderRadius: 10, border: 'none', color: '#fff',
                background: busy ? '#9bb4e6' : '#2563eb', cursor: busy ? 'default' : 'pointer' }}>
              🎙 Talk to the assistant
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: voice === 'speaking' ? '#1e7e34' : '#1f5b8f' }}>
                {voice === 'speaking' ? '🔊 ' : '🎙 '}{VOICE_STATUS[voice]}
              </div>
              <button onClick={endVoice}
                style={{ padding: '12px 22px', fontSize: 16, fontWeight: 700, borderRadius: 10, border: 'none', color: '#fff', background: '#d9534f', cursor: 'pointer' }}>
                End
              </button>
            </div>
          )}
          {!inCall && dropped && (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: '#fff7e6', border: '1px solid #f0ad4e', color: '#8a5a00', fontSize: 13, fontWeight: 700 }}>
              Call ended - tap Talk to pick up where you left off.
            </div>
          )}
          {(wakeOk === false || !wakeLockSupported()) && (
            <div style={{ fontSize: 12, color: '#8a5a00', marginTop: 6 }}>Keep your screen on during the call.</div>
          )}
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: 1.45 }}>
            {inCall
              ? 'The form fills in as you talk. You can’t edit it until you end the call.'
              : 'Have a conversation about your day, in English or Spanish. Nothing is sent until you tap Submit Daily Report.'}
          </div>
        </div>
      )}

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 14, background: '#f6f7f9' }}>
        {!voiceEnabled && (
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
            Tell me about your day — talk with the mic on your keyboard or type. I fill in the form; nothing is sent until you tap <strong>Submit Daily Report</strong>.
          </div>
        )}
        {voiceLines.map((m, i) => bubble(m, `v${i}`))}
        {partial && bubble(partial, 'partial', true)}
        {messages.map((m, i) => bubble(m, i))}
        {busy && <div style={{ fontSize: 13, color: '#6b7280' }}>Thinking…</div>}
        {error && (
          <div style={{ marginTop: 8, padding: '9px 12px', borderRadius: 8, background: '#fdecec', border: '1px solid #d9534f', color: '#a12622', fontSize: 13 }}>
            {errText(error)}
            {!voiceEnabled && messages.length === 0 && !busy && (
              <button onClick={retryGreeting} style={{ marginLeft: 8, border: 'none', background: 'none', color: '#a12622', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>Try again</button>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: 10, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={inCall ? 'End the call to type…' : 'Type or tap the mic to talk…'}
          rows={3}
          maxLength={4000}
          disabled={inCall}
          style={{ flex: 1, resize: 'none', padding: '9px 10px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd2da', fontFamily: 'inherit' }}
        />
        <button onClick={send} disabled={busy || inCall || !input.trim()}
          style={{ padding: '10px 16px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', color: '#fff',
            background: busy || inCall || !input.trim() ? '#9bb4e6' : '#2563eb', cursor: busy || inCall || !input.trim() ? 'default' : 'pointer' }}>
          {busy ? '…' : 'Send'}
        </button>
      </div>
    </div>
  )
}
